import { useState } from 'react'
import { ONRECORD_BRAND } from '../../constants/BrandColors'

interface AvatarProps {
  src?: string
  alt: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  onClick?: () => void
}

/**
 * on record Avatar Component
 * Version: 2.0 - Branded Orange Circles with White Initials
 *
 * Features:
 * - on record branded orange background (#FCA903)
 * - White initials for users without avatar images
 * - Fallback to initials if image fails to load
 * - Professional appearance matching mobile app branding
 */
export default function Avatar({ src, alt, size = 'md', className = '', onClick }: AvatarProps) {
  const [imageError, setImageError] = useState(false)
  const sizeClass = `avatar-${size}`

  const handleImageError = () => {
    if (import.meta.env.DEV) {
      console.log('Avatar image failed to load, showing branded fallback:', src)
    }
    setImageError(true)
  }

  // Extract initials from name (e.g., "ravi shankar" -> "RS")
  const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }

  // Show image if available, fall back to branded initials on error
  const showFallback = !src || imageError

  return (
    <div
      className={`avatar ${sizeClass} ${className}`}
      onClick={(e) => { if (onClick) { e.stopPropagation(); onClick() } }}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {!showFallback ? (
        <img
          src={src}
          alt={alt}
          onError={handleImageError}
          referrerPolicy="no-referrer"
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center font-bold"
          style={{
            backgroundColor: ONRECORD_BRAND.AVATAR_BACKGROUND,
            color: ONRECORD_BRAND.AVATAR_TEXT
          }}
        >
          {getInitials(alt)}
        </div>
      )}
    </div>
  )
}
